import React, { ReactElement } from 'react';
import { Avatar, ReviewsStars, Text } from '@feast-it/ui/light';
import { useRouter } from 'next/router';

import { initializeSurname } from '../../modules/formatting';
import { optimizeImageUrl } from '../../modules/imageLoader';
import Link from '../Link';

import { trackEvent } from '../../modules/analytics';
import { SUPPLIER_PROFILE_CLICKED } from '../../constants/analytics';
import { getAbsoluteUrl } from '@/helpers/formatUrl';
import { Review } from '../../containers/vertical/Reviews/Reviews';
import { PATHS } from '../../constants/paths';
import {
  AvatarWrapper,
  Content,
  CustomerDetailsWrapper,
  CustomerText,
  StarsWrapper,
  TestimonialQuoteContainer,
  StyledQuote,
} from './TestimonialQuote.styled';

interface TestimonialQuoteProps {
  review: Review;
}

export function TestimonialQuote({
  review,
}: TestimonialQuoteProps): ReactElement {
  const router = useRouter();
  const href = PATHS.supplierProfile(review.supplierSlug);

  return (
    <TestimonialQuoteContainer>
      <Content>
        <StyledQuote as="p" color="black" style="bodyMedium">
          {review.quote}
        </StyledQuote>

        {!!review.rating && (
          <StarsWrapper>
            <ReviewsStars
              rating={review.rating}
              size={24}
              color="mediumYellow"
            />
          </StarsWrapper>
        )}

        <CustomerDetailsWrapper>
          <AvatarWrapper>
            <Avatar
              imageProps={{
                src:
                  review.image.url && review.image.url.includes('imgix.net')
                    ? optimizeImageUrl({
                        src: review.image.url,
                        width: 44,
                        quality: 75,
                      })
                    : review.image.url,
                alt: `${review.author} profile picture`,
              }}
            />
          </AvatarWrapper>

          {review.author && review.supplierName && (
            <div>
              <CustomerText>
                <Text style="bodySmall" color="darkGrey">
                  {initializeSurname(review.author)}
                </Text>

                <Text style="bodySmall" color="darkGrey">
                  &bull;
                </Text>

                <Text style="bodySmall" color="darkGrey">
                  {review.eventType}
                </Text>
              </CustomerText>

              <Link
                href={href}
                color="primary"
                wrap="true"
                onClick={() =>
                  trackEvent(SUPPLIER_PROFILE_CLICKED, {
                    supplier: review.supplierName,
                    destinationUrl: getAbsoluteUrl(href),
                    currentPage: getAbsoluteUrl(router.asPath),
                    source: 'review',
                  })
                }
              >
                <Text as="p" style="bodyMedium" color="mediumBlue">
                  {review.supplierName}
                </Text>
              </Link>
            </div>
          )}
        </CustomerDetailsWrapper>
      </Content>
    </TestimonialQuoteContainer>
  );
}
